import { Subject } from 'rxjs';
import { SupportTicket } from '../models/support-ticket.model';

const STORAGE_PREFIX = 'mmm.support.read.';

/** Emits when local read state changes so sidebar/list can refresh. */
const readChangedSubject = new Subject<void>();
export const supportReadChanged$ = readChangedSubject.asObservable();

function storageKey(uid: string, ticketId: string): string {
  return `${STORAGE_PREFIX}${uid}.${ticketId}`;
}

export function toSupportMillis(value: unknown): number {
  try {
    if (value == null) return 0;
    if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date ? d.getTime() : 0;
    }
    if (typeof (value as { seconds?: number }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000;
    }
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  } catch {
    // ignore
  }
  return 0;
}

export function getSupportLastReadAt(uid: string, ticketId: string): number {
  if (!uid || !ticketId || typeof localStorage === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(storageKey(uid, ticketId));
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Optimistic local cache; Firestore churchLastReadAt is the source of truth. */
export function markSupportTicketReadLocal(
  uid: string,
  ticketId: string,
  at: number = Date.now()
): void {
  if (!uid || !ticketId || typeof localStorage === 'undefined') return;
  const ts = Number.isFinite(at) && at > 0 ? at : Date.now();
  try {
    localStorage.setItem(storageKey(uid, ticketId), String(ts));
    readChangedSubject.next();
  } catch {
    // quota / private mode
  }
}

/** @deprecated Use markSupportTicketReadLocal; kept for callers. */
export function markSupportTicketRead(
  uid: string,
  ticketId: string,
  at: number = Date.now()
): void {
  markSupportTicketReadLocal(uid, ticketId, at);
}

export function isSupportTicketUnread(
  ticket: SupportTicket | null | undefined,
  uid: string | null | undefined
): boolean {
  if (!ticket?.id || !uid) return false;
  if (ticket.status && ticket.status !== 'open') return false;
  if (ticket.lastMessageByRole !== 'support') return false;

  const lastAt = toSupportMillis(ticket.lastMessageAt ?? ticket.updatedAt);
  if (!lastAt) return true;

  const serverReadAt = toSupportMillis(ticket.churchLastReadAt);
  if (serverReadAt > 0) {
    return lastAt > serverReadAt;
  }

  // Compat: tickets aún sin churchLastReadAt → localStorage.
  const localReadAt = getSupportLastReadAt(uid, ticket.id);
  return lastAt > localReadAt;
}

export function countUnreadSupportTickets(
  tickets: SupportTicket[] | null | undefined,
  uid: string | null | undefined
): number {
  if (!tickets?.length || !uid) return 0;
  return tickets.reduce((n, t) => (isSupportTicketUnread(t, uid) ? n + 1 : n), 0);
}

export function firstUnreadSupportTicket(
  tickets: SupportTicket[] | null | undefined,
  uid: string | null | undefined
): SupportTicket | null {
  if (!tickets?.length || !uid) return null;
  return tickets.find((t) => isSupportTicketUnread(t, uid)) || null;
}
